import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type NodeAgentRecord,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'
import type {
  CreateNodeAgentRequest,
  DispatchNodeAgentRequest,
  UpdateNodeAgentRequest,
} from './node-agents.client'

/**
 * Admin-only node-agents client: addresses any company via ?companyId=
 * (the super-admin company switcher is client-side selection, not a session
 * org change). Mirrors agents-admin.client.
 */
export class NodeAgentsAdminApiClient extends BaseApiClient {
  private withCompany(path: string, companyId: string): string {
    return `${path}?companyId=${encodeURIComponent(companyId)}`
  }

  async list(companyId: string): Promise<NodeAgentRecord[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord[]>>(
          this.withCompany(API_ROUTES.ADMIN.NODE_AGENTS.GET_ALL, companyId),
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching node agents (admin)',
      },
    )
    return result ?? []
  }

  async get(companyId: string, id: string): Promise<NodeAgentRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeAgentRecord>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.NODE_AGENTS.GET, { id }),
            companyId,
          ),
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching node agent (admin)',
      },
    )
  }

  async create(
    companyId: string,
    dto: CreateNodeAgentRequest,
  ): Promise<NodeAgentRecord | null> {
    const response = await this.request<ApiResponse<NodeAgentRecord>>(
      this.withCompany(API_ROUTES.ADMIN.NODE_AGENTS.CREATE, companyId),
      { method: 'POST', body: JSON.stringify(dto) },
    )
    return response.data ?? null
  }

  async update(
    companyId: string,
    id: string,
    dto: UpdateNodeAgentRequest,
  ): Promise<NodeAgentRecord | null> {
    const response = await this.request<ApiResponse<NodeAgentRecord>>(
      this.withCompany(
        interpolateRoute(API_ROUTES.ADMIN.NODE_AGENTS.UPDATE, { id }),
        companyId,
      ),
      { method: 'PATCH', body: JSON.stringify(dto) },
    )
    return response.data ?? null
  }

  async remove(companyId: string, id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ deleted: boolean }>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.NODE_AGENTS.DELETE, { id }),
            companyId,
          ),
          { method: 'DELETE' },
        )
        return response.data?.deleted ?? false
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting node agent (admin)',
      },
    )
    return result ?? false
  }

  async dispatch(
    companyId: string,
    id: string,
    dto: DispatchNodeAgentRequest,
  ): Promise<{ roomName: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ roomName: string }>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.NODE_AGENTS.DISPATCH, { id }),
            companyId,
          ),
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error dispatching node agent call (admin)' },
    )
  }
}

export const nodeAgentsAdminApiClient = new NodeAgentsAdminApiClient()
